<script lang="ts">
  import { Citas, Pacientes } from '../../../lib/api';
  import { tenantCtx } from '../../../lib/tenant.svelte';
  import { urlAgendarPublico } from '../../../lib/citas-publico';
  import KpiCard from '../widgets/KpiCard.svelte';
  import QuickActions from '../widgets/QuickActions.svelte';
  import { session } from '../../../lib/auth.svelte';
  import { rubroFromTipo } from '../../../lib/citas-rubro';
  import { loadLocalBranding } from '../../../lib/branding.svelte';

  const local = $derived(loadLocalBranding());

  const modulos = $derived(tenantCtx.modulosActivos ?? []);
  const tiene = (m: string) => modulos.length === 0 || modulos.includes(m);
  let { onNavigate }: { onNavigate: (tab: string) => void } = $props();

  const nombre = $derived(tenantCtx.displayName || 'Mi clínica');
  const link = $derived(urlAgendarPublico(tenantCtx.snapshot?.tenantId ?? ''));
  const planLabel = $derived(tenantCtx.plan === 'clinica-solo' ? 'Plan Solo' : tenantCtx.plan === 'clinica-plus' ? 'Plan Plus' : 'Plan Clínica');
  const fecha = $derived(new Date().toLocaleDateString('es-GT', { weekday:'long', day:'numeric', month:'long', year:'numeric' }));
  const usuario = $derived(session.value?.usuario ?? 'Usuario');
  const rubro = $derived(rubroFromTipo(tenantCtx.orgTipo));

  const specialtyIcons: Record<string, string> = {
    'clinica-medica': '🩺',
    'clinica-odontologica': '🦷',
    'clinica-veterinaria': '🐶',
    'clinica-optica': '👁️',
    'clinica-nutricion': '🥗',
    'clinica-psicologia': '🧠',
    'clinica-estetica': '💉',
    'clinica-fisioterapia': '💪',
  };
  const specialtyIcon = $derived(specialtyIcons[tenantCtx.orgTipo ?? ''] || '🏥');

  let ok = $state(false);
  let citas = $state(0);
  let pacientes = $state(0);
  let lista = $state<Array<{h:string;n:string;t:string}>>([]);
  const hoy = () => new Date().toISOString().slice(0,10);

  const actions = $derived([
    { id: 'agenda', label: 'Agenda', icon: '' },
    ...(tiene('admision') ? [{ id: 'pacientes', label: 'Pacientes', icon: '' }] : []),
    ...(tiene('expediente') ? [{ id: 'expediente', label: 'Expediente', icon: '' }] : []),
    ...(tiene('facturacion') ? [{ id: 'cuentas', label: 'Cobros', icon: '' }] : []),
  ]);

  $effect(()=>{Promise.all([tiene('citas')?Citas.listar(hoy()).catch(()=>[]):[],tiene('admision')?Pacientes.list().catch(()=>[]):[]]).then(([c,p])=>{citas=c.length;pacientes=p.length;lista=c.slice(0,10).map((x:any)=>({h:x.fechaHora?new Date(x.fechaHora).toLocaleTimeString('es-GT',{hour:'2-digit',minute:'2-digit'}):'—',n:x.pacienteNombre??'—',t:x.motivo||''}));ok=true})});
</script>

<div class="clinic-dash">
  <!-- ===== HEADER MEJORADO ===== -->
  <div class="clinic-header">
    <div class="header-bg"></div>
    <div class="header-content">
      <div class="header-top">
        <div class="header-icon">{specialtyIcon}</div>
        <div class="header-info">
          <h1 class="header-title">{nombre}</h1>
          <p class="header-subtitle">{rubro.label} · {fecha}</p>
        </div>
        <span class="header-plan">{planLabel}</span>
      </div>
      <div class="header-stats">
        <div class="stat-item">
          <span class="stat-value">{ok ? citas : '—'}</span>
          <span class="stat-label">Citas hoy</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value">{ok ? pacientes : '—'}</span>
          <span class="stat-label">Pacientes</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value">{ok ? 'Activo' : '—'}</span>
          <span class="stat-label">Estado</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value">{usuario.split('@')[0]}</span>
          <span class="stat-label">Usuario</span>
        </div>
      </div>

      {#if local.horario || local.direccion || local.telefono}
        <div class="header-info-bar">
          {#if local.horario}<span class="info-chip">🕐 {local.horario}</span>{/if}
          {#if local.direccion}<span class="info-chip">📍 {local.direccion}</span>{/if}
          {#if local.telefono}<span class="info-chip">📞 {local.telefono}</span>{/if}
          {#if local.email}<span class="info-chip">✉️ {local.email}</span>{/if}
        </div>
      {/if}
    </div>
  </div>

  <!-- ===== KPIs ===== -->
  <div class="dash-kpis">
    <KpiCard label="Citas hoy" value={ok ? citas : '—'} hint="Programadas" tone="brand" />
    <KpiCard label="Pacientes" value={ok ? pacientes : '—'} hint="Registrados" tone="success" />
    <KpiCard label="Estado" value={ok ? 'Activo' : '—'} hint="Sistema operativo" tone="neutral" />
  </div>

  <!-- ===== Acciones rápidas ===== -->
  <QuickActions actions={actions} onAction={onNavigate} />

  <!-- ===== Agenda de hoy ===== -->
  <div class="glass-card">
    <div class="glass-card-head">
      <span>Agenda de hoy</span>
      <button class="link-btn" onclick={() => onNavigate('agenda')}>Ver todo →</button>
    </div>
    <div class="glass-card-body">
      <table class="glass-table">
        <thead>
          <tr>
            <th>Hora</th>
            <th>Paciente</th>
            <th>Motivo</th>
          </tr>
        </thead>
        <tbody>
          {#if !ok}
            <tr><td colspan="3" class="empty">Cargando...</td></tr>
          {:else if lista.length===0}
            <tr><td colspan="3" class="empty">Sin citas programadas para hoy</td></tr>
          {:else}
            {#each lista as c}
              <tr>
                <td class="mono">{c.h}</td>
                <td>{c.n}</td>
                <td class="muted">{c.t}</td>
              </tr>
            {/each}
          {/if}
        </tbody>
      </table>
    </div>
  </div>

  {#if link}
    <div class="link-bar">
      <span>Enlace pacientes</span>
      <code>{link}</code>
      <button class="glass-btn-small" onclick={() => navigator.clipboard.writeText(link!)}>Copiar</button>
    </div>
  {/if}
</div>

<style>
  .clinic-dash {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    padding: 0.25rem;
  }

  /* ===== HEADER ===== */
  .clinic-header {
    position: relative;
    border-radius: var(--radius-xl);
    overflow: hidden;
    margin-bottom: 0.25rem;
  }

  .header-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #0d9488 0%, #0f766e 40%, #0c4a6e 100%);
    background-size: 200% 200%;
    animation: headerShift 10s ease infinite;
    z-index: 0;
  }
  .header-bg::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
                radial-gradient(circle at 70% 60%, rgba(20,184,166,0.2) 0%, transparent 40%),
                radial-gradient(circle at 10% 80%, rgba(56,189,248,0.12) 0%, transparent 35%);
    z-index: 1;
  }
  .header-bg::after {
    content: '';
    position: absolute;
    inset: 0;
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    background: rgba(255,255,255,0.06);
    z-index: 2;
  }

  @keyframes headerShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  .header-content {
    position: relative;
    z-index: 3;
    padding: 1.5rem 1.75rem;
    color: #fff;
  }

  .header-top {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.15rem;
  }

  .header-icon {
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    border-radius: 16px;
    background: rgba(255,255,255,0.18);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.2);
    flex-shrink: 0;
  }

  .header-info { flex: 1; min-width: 0; }

  .header-title {
    font-size: 1.45rem !important;
    font-weight: 800 !important;
    margin: 0 0 0.15rem !important;
    color: #fff !important;
    letter-spacing: -0.02em;
    text-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }

  .header-subtitle {
    margin: 0 !important;
    font-size: 0.82rem !important;
    color: rgba(255,255,255,0.75) !important;
    text-transform: capitalize;
  }

  .header-plan {
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;
    background: rgba(255,255,255,0.18);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.25);
    color: #fff;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .header-stats {
    display: flex;
    align-items: center;
    gap: 0;
    padding: 0.85rem 1.1rem;
    background: rgba(255,255,255,0.12);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 0.85rem;
    flex-wrap: wrap;
  }

  .stat-item {
    flex: 1;
    min-width: 70px;
    text-align: center;
    padding: 0.15rem 0.5rem;
  }

  .stat-value {
    display: block;
    font-size: 1.35rem;
    font-weight: 800;
    color: #fff;
    line-height: 1.15;
    text-shadow: 0 1px 4px rgba(0,0,0,0.1);
  }

  .stat-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(255,255,255,0.6);
    margin-top: 0.15rem;
  }

  .stat-divider {
    width: 1px;
    height: 32px;
    background: rgba(255,255,255,0.2);
    flex-shrink: 0;
  }

  .header-info-bar { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.75rem; }
  .info-chip { font-size: 0.7rem; padding: 0.3rem 0.65rem; border-radius: 999px;
    background: rgba(255,255,255,0.12); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
    border: 1px solid rgba(255,255,255,0.15); color: rgba(255,255,255,0.85); white-space: nowrap; }

  /* ===== Glass components ===== */
  .glass-card {
    background: var(--glass-bg-card);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-xl);
    overflow: hidden;
    box-shadow: var(--glass-shadow);
    transition: box-shadow 0.3s ease;
  }
  .glass-card:hover { box-shadow: var(--glass-shadow-lg); }

  .glass-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.75rem 1.1rem;
    border-bottom: 1px solid var(--glass-border);
    background: rgba(13,148,136,0.04);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    font-weight: 700;
    color: var(--text-primary);
    font-size: 0.88rem;
  }

  .glass-card-body { padding: 0; }

  .glass-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
  .glass-table th { text-align: left; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-secondary); padding: 0.55rem 0.85rem; background: rgba(13,148,136,0.04); font-weight: 700; border-bottom: 1px solid var(--glass-border); }
  .glass-table td { padding: 0.6rem 0.85rem; border-top: 1px solid rgba(15,23,42,0.04); color: var(--text-primary); }
  .glass-table tr:hover td { background: rgba(255,255,255,0.4); }

  .mono { font-family: ui-monospace, 'JetBrains Mono', monospace; font-size: 0.8rem; }
  .muted { color: var(--text-muted); font-size: 0.82rem; }
  .empty { text-align: center; padding: 2.5rem 1rem; color: var(--text-muted); font-style: italic; }

  .link-btn { font-size: 0.75rem; color: var(--brand); text-decoration: none; font-weight: 600; background: none; border: none; cursor: pointer; padding: 0; transition: color 0.2s; }
  .link-btn:hover { color: var(--brand-deep); }

  .link-bar {
    display: flex; align-items: center; gap: 0.65rem; padding: 0.65rem 1rem;
    background: var(--glass-bg-card); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
    border: 1px solid var(--glass-border); border-radius: var(--radius-xl); box-shadow: var(--glass-shadow);
    font-size: 0.78rem; color: var(--text-secondary);
  }
  .link-bar code { flex: 1; font-size: 0.7rem; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; background: rgba(0,0,0,0.03); padding: 0.25rem 0.5rem; border-radius: 0.35rem; }

  .glass-btn-small {
    font-size: 0.72rem; padding: 0.3rem 0.7rem; border-radius: 999px;
    border: 1px solid var(--glass-border); background: var(--glass-bg-card);
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    cursor: pointer; color: var(--text-secondary); font-weight: 600; white-space: nowrap; transition: all 0.2s ease;
  }
  .glass-btn-small:hover { background: var(--brand-surface); border-color: var(--brand-strong); color: var(--brand-deep); }

  @media (max-width: 600px) {
    .header-stats { flex-direction: column; gap: 0.5rem; }
    .stat-divider { width: 80%; height: 1px; }
    .header-top { flex-direction: column; text-align: center; }
    .header-icon { margin: 0 auto; }
  }
</style>
